/**
 * The engine's state transitions. Each function takes a GameState and returns a
 * new one (never mutating its input; unchanged sub-objects are shared). The Rng
 * is rebuilt from `rngState` and its state written back after every draw, so a
 * GameState is plain JSON and can be persisted, sent, or replayed.
 *
 * Spec: docs/GAME_DESIGN.md § 1 (core loop, resolution table, checkpoints).
 */
import { ADAPTIVE, LEVEL_WIDTH } from '../config.ts';
import { computeCheckpointStats, decisionFor, evaluateCheckpoint, isPaceTimeout, jumpCeiling, normalizedDelta } from '../adaptive/checkpoint.ts';
import { clampRating, levelForRating } from '../adaptive/rating.ts';
import { getLevel, ratingForLevel } from '../levels/levels.ts';
import { fallTimeMs } from '../adaptive/pace.ts';
import { generateProblem } from '../algebra/generator.ts';
import { Rng } from '../math/rng.ts';
import type { Rational } from '../math/rational.ts';
import { levelUpBonus, ratingAnchor, scoreAnswer } from '../scoring/index.ts';
import type {
  ActiveBlock,
  AnswerRecord,
  CheckpointResult,
  GameConfig,
  GameState,
  InputVia,
  Outcome,
  Resolution,
  ScoreDelta,
} from '../types.ts';
import { lockTime, rowOfBlock, timeLeftOfBlock } from './geometry.ts';
import { runRatedScore } from './summary.ts';

/** Duration of the `resolving` phase for an outcome (part of the rules — replayed by the server). */
export function resolveDurationMs(config: GameConfig, outcome: Outcome): number {
  if (outcome === 'correct') return config.correctResolveMs;
  return outcome === 'wrong' ? config.wrongResolveMs : config.timeoutResolveMs;
}

/** Engine time of the next automatic transition (lock or phase end); Infinity when the game is over. */
export function nextTransitionAt(state: GameState): number {
  if (state.phase === 'gameOver') return Number.POSITIVE_INFINITY;
  if (state.phase === 'falling') return state.active ? state.active.lockAt : state.phaseEndsAt;
  return state.phaseEndsAt;
}

/** Draw the problem with sequence number `seq` at `level`, returning it with the advanced rng state. */
export function drawProblem(rngState: number, level: number, seq: number): { problem: GameState['next']; rngState: number } {
  const rng = new Rng(rngState);
  const problem = generateProblem(rng, getLevel(level), seq);
  return { problem, rngState: rng.state };
}

/**
 * Spawn the preview problem as the active block at `at` (phase `falling`) and
 * draw a new preview. Clears `pendingLevelChange`: by now its banner has shown.
 */
export function spawnNext(state: GameState, at: number): GameState {
  const problem = state.next;
  const paced = fallTimeMs(problem, getLevel(state.level), {
    runAnswered: state.history.length,
    levelAnswered: state.levelAnswered,
    rating: state.rating,
  });
  // "More time" scales the clamped fall time, so a relaxed block always gets
  // paceScale × the standard time (1 leaves standard runs bit-for-bit unchanged).
  const fall = state.config.paceScale === 1 ? paced : Math.round(paced * state.config.paceScale);
  const active: ActiveBlock = {
    problem,
    spawnedAt: at,
    fallTimeMs: fall,
    lockAt: lockTime(at, fall, state.bricks, state.config),
    bricksAtSpawn: state.bricks,
  };
  const drawn = drawProblem(state.rngState, state.level, state.nextSeq);
  return {
    ...state,
    now: at,
    phase: 'falling',
    phaseEndsAt: active.lockAt,
    active,
    next: drawn.problem,
    nextSeq: state.nextSeq + 1,
    rngState: drawn.rngState,
    pendingLevelChange: null,
  };
}

/** End of a `resolving` / `levelBanner` phase: show the level banner if a checkpoint changed the level, else spawn. */
export function finishPhase(state: GameState): GameState {
  const at = state.phaseEndsAt;
  if (state.phase === 'resolving' && state.pendingLevelChange) {
    return { ...state, now: at, phase: 'levelBanner', phaseEndsAt: at + state.config.levelBannerMs };
  }
  return spawnNext(state, at);
}

function withLevelUpPart(delta: ScoreDelta, bonus: number): ScoreDelta {
  return { ...delta, raw: delta.raw + bonus, parts: [...delta.parts, { label: 'levelUp', raw: bonus }] };
}

/** Apply a level change (checkpoint or probation revert): move level, record it, redraw the preview. */
function changeLevel(state: GameState, result: CheckpointResult, extra: Partial<GameState>): GameState {
  const changed = result.levelAfter !== result.levelBefore;
  const redraw = changed ? drawProblem(state.rngState, result.levelAfter, state.nextSeq) : null;
  return {
    ...state,
    rating: result.ratingAfter,
    level: result.levelAfter,
    maxLevel: Math.max(state.maxLevel, result.levelAfter),
    checkpoints: [...state.checkpoints, result],
    window: [],
    levelAnswered: changed ? 0 : state.levelAnswered,
    pendingLevelChange: changed ? result : null,
    next: redraw ? redraw.problem : state.next,
    nextSeq: redraw ? state.nextSeq + 1 : state.nextSeq,
    rngState: redraw ? redraw.rngState : state.rngState,
    ...extra,
  };
}

/**
 * Checkpoint after `checkpointSize` answers: re-rate, move level, award the
 * level-up bonus (also folded into lastResolution.scoreDelta so the UI can show
 * it), and redraw the preview at the new level. Meeting the target raises the
 * demonstrated peak; a jump of ≥ PROBATION_MIN_JUMP levels starts a probation.
 */
function applyCheckpoint(state: GameState): GameState {
  const ceilingLevel = jumpCeiling(state.checkpoints, state.referenceRating);
  const result = evaluateCheckpoint(state.window, state.rating, state.level, undefined, { ceilingLevel });
  const met = normalizedDelta(result.stats) >= 0;
  const bonus = levelUpBonus(result.levelBefore, result.levelAfter);
  const lastResolution =
    bonus > 0 && state.lastResolution
      ? { ...state.lastResolution, scoreDelta: withLevelUpPart(state.lastResolution.scoreDelta, bonus) }
      : state.lastResolution;
  const jumped = result.levelAfter - result.levelBefore >= ADAPTIVE.PROBATION_MIN_JUMP;
  return changeLevel(state, result, {
    peakRating: met ? Math.max(state.peakRating, result.ratingBefore) : state.peakRating,
    raw: state.raw + bonus,
    lastResolution,
    probation: jumped ? { fromRating: result.ratingBefore, fromLevel: result.levelBefore, answers: 0, misses: 0 } : null,
  });
}

/**
 * Jump probation bookkeeping for one answer at the new level. PROBATION_MISSES
 * misses within the first PROBATION_ANSWERS cancel the jump: the player goes
 * back to one level above where they jumped from (they did well there), the
 * failed level becomes this run's jump ceiling (via the recorded result), and a
 * new checkpoint window starts. No level-up bonus is clawed back.
 */
function applyProbation(state: GameState, record: AnswerRecord): GameState {
  const p = state.probation;
  if (!p) return state;
  const miss = record.outcome === 'wrong' || (record.outcome === 'timeout' && !isPaceTimeout(record));
  const probation = { ...p, answers: p.answers + 1, misses: p.misses + (miss ? 1 : 0) };
  if (probation.misses >= ADAPTIVE.PROBATION_MISSES) {
    const levelAfter = Math.min(p.fromLevel + 1, state.level - 1);
    const ratingAfter = clampRating(Math.max(p.fromRating, ratingForLevel(levelAfter) + LEVEL_WIDTH / 2));
    const result: CheckpointResult = {
      stats: computeCheckpointStats(state.window),
      ratingBefore: state.rating,
      ratingAfter,
      levelBefore: state.level,
      levelAfter: levelForRating(ratingAfter, levelAfter),
      decision: decisionFor(state.level, levelAfter),
    };
    return changeLevel(state, result, { probation: null });
  }
  return { ...state, probation: probation.answers >= ADAPTIVE.PROBATION_ANSWERS ? null : probation };
}

/**
 * Resolve the active block at `at` as correct / wrong / timeout. Brick changes
 * apply at the START of resolving, so game over (bricks ≥ wellRows − 1) is
 * detected immediately; the resolution is still recorded for the UI.
 *
 * `rated` is recomputed from the whole history after the checkpoint (if any),
 * so it reflects the peak rating that checkpoint may have raised: the live HUD
 * value, equal to the final RatedScore once the run is over.
 */
export function resolveActive(
  state: GameState,
  outcome: Outcome,
  at: number,
  given: Rational | null,
  via: InputVia | null,
): GameState {
  const block = state.active;
  if (!block) throw new Error('resolveActive: no active block');
  const { problem } = block;
  const { config } = state;
  const correct = outcome === 'correct';
  const bricksBefore = state.bricks;
  const brokeLayer = correct && bricksBefore > 0;
  const bricks = correct ? bricksBefore - (brokeLayer ? 1 : 0) : bricksBefore + 1;
  const streak = correct ? state.streak + 1 : 0;

  const record: AnswerRecord = {
    problemId: problem.id,
    seq: problem.seq,
    templateId: problem.templateId,
    level: problem.level,
    difficultyIndex: getLevel(problem.level).difficultyIndex,
    parTimeMs: problem.parTimeMs,
    responseMs: at - block.spawnedAt,
    outcome,
    given,
    via,
    remainingFraction: outcome === 'timeout' ? 0 : timeLeftOfBlock(block, at),
    at,
  };
  // The relevance anchor for this answer's weighted contribution: max(reference, peak so far).
  const anchor = ratingAnchor(state.referenceRating, state.peakRating);
  const scoreDelta = scoreAnswer({ record, streak, bricksBefore, brokeLayer, referenceRating: anchor });
  const lastResolution: Resolution = {
    kind: outcome,
    problem,
    given,
    at,
    fromRow: rowOfBlock(block, config, at),
    brokeLayer,
    scoreDelta,
  };

  let next: GameState = {
    ...state,
    now: at,
    phase: 'resolving',
    phaseEndsAt: at + resolveDurationMs(config, outcome),
    active: null,
    bricks,
    streak,
    bestStreak: Math.max(state.bestStreak, streak),
    layersBroken: state.layersBroken + (brokeLayer ? 1 : 0),
    raw: state.raw + scoreDelta.raw,
    window: [...state.window, record],
    history: [...state.history, record],
    levelAnswered: state.levelAnswered + 1,
    lastResolution,
  };
  next = applyProbation(next, record);
  if (next.window.length >= config.checkpointSize) next = applyCheckpoint(next);
  next = { ...next, rated: runRatedScore(next) };
  if (next.bricks >= config.wellRows - 1) next = { ...next, phase: 'gameOver', phaseEndsAt: at };
  return next;
}
